import React from "react";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";

interface PlanApprovalCardProps {
  message: string;
  plan: {
    id: string;
    goal: string;
    steps: Array<{
      number: number;
      id: string;
      tool: string;
      description: string;
      arguments: Record<string, any>;
      dependsOn?: string[];
    }>;
    hasConcerns?: boolean;
    concerns?: string;
  };
  actions: Array<{
    id: string;
    label: string;
    type: "primary" | "secondary" | "tertiary";
    icon?: string;
    description?: string;
  }>;
  onAction: (actionId: string) => void;
  isDark: boolean;
}

export const PlanApprovalCard: React.FC<PlanApprovalCardProps> = ({
  message,
  plan,
  actions,
  onAction,
  isDark,
}) => {
  return (
    <div
      className={`rounded-lg p-4 ${
        isDark
          ? "bg-gray-800 border border-gray-700"
          : "bg-white border border-gray-200"
      } shadow-sm`}
    >
      {/* Message */}
      <p
        className={`text-sm mb-3 ${isDark ? "text-gray-200" : "text-gray-800"}`}
      >
        {message}
      </p>

      {/* Plan Steps */}
      <div
        className={`rounded-md p-3 mb-3 ${
          isDark ? "bg-gray-900/50" : "bg-gray-50"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`text-xs font-medium ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            📋 Plan Steps
          </span>
        </div>
        <div className="space-y-2">
          {plan.steps.map((step) => (
            <div
              key={step.id}
              className={`flex items-start gap-2 text-xs ${
                isDark ? "text-gray-300" : "text-gray-700"
              }`}
            >
              <span
                className={`font-medium ${
                  isDark ? "text-accent-400" : "text-accent-600"
                }`}
              >
                {step.number}.
              </span>
              <span>{step.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Warning if concerns */}
      {plan.hasConcerns && (
        <div
          className={`flex items-start gap-2 p-2 rounded-md mb-3 ${
            isDark
              ? "bg-warning-900/20 border border-yellow-700/30"
              : "bg-yellow-50 border border-yellow-200"
          }`}
        >
          <AlertTriangle
            className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
              isDark ? "text-warning-400" : "text-warning-600"
            }`}
          />
          <span
            className={`text-xs ${
              isDark ? "text-warning-300" : "text-warning-700"
            }`}
          >
            May need adjustments
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        {actions.map((action) => (
          <button
            key={action.id}
            onClick={() => onAction(action.id)}
            className={`
              flex-1 px-4 py-2 rounded-md text-sm font-medium
              transition-colors duration-200
              ${
                action.type === "primary"
                  ? isDark
                    ? "bg-accent-600 hover:bg-accent-700 text-white"
                    : "bg-accent-500 hover:bg-accent-600 text-white"
                  : isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
              }
            `}
            title={action.description}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};
